/**
 * Has nobody checked an open run for longer than a shift? (rule 17)
 *
 * ONE copy of the rule, read by the operator line picker (LinePick.tsx) and the office Runs register
 * (Runs.tsx). Until 31 Oct 2026 each kept its own; two copies of a rule drift (UI/UX audit,
 * 31 Oct 2026, UX-36 follow-up). It lives in lib/ so the operator side imports it without pulling
 * office code.
 *
 * ⚠ NOT "IS IT OLD". Runs carry on across shift changes (Amit, 2026-09-24): the next shift continues
 * the same run, so a run started at 20:00 and still going at 07:00 is ordinary. The first version of
 * this measured AGE against an assumed 12-hour shift and told the operator of exactly that run "do
 * not carry on in it". Silence is the better signal - in-process checks recur every few hours, so a
 * run with no check for a whole shift has probably stopped. Run 2 sat OPEN from 27 Oct with its last
 * reading at 22:27 that night, and its office row looked the same as a healthy run.
 */

/** One shift: 8 hours (06:00 / 14:00 / 22:00, fleet-docs definitions/plant-structure.md). */
export const SHIFT_MS = 8 * 60 * 60 * 1000;

/** When the run was last worked on, in ms: its newest reading, or its start; 0 when neither parses. */
export function lastActivity(r: { startedAt: string; lastReadingAt?: string | null }): number {
  const started = Date.parse(r.startedAt);
  const read = r.lastReadingAt ? Date.parse(r.lastReadingAt) : NaN;
  return Math.max(Number.isNaN(started) ? 0 : started, Number.isNaN(read) ? 0 : read);
}

/** How long an OPEN run has gone without a check, in ms, when that is more than a shift; else null.
 *  A closed run is never flagged: it stopped, which is what the flag asks about. A run with no date
 *  that parses is not known, never "quiet". */
export function quietLongerThanAShift(
  r: { status: 'OPEN' | 'CLOSED'; startedAt: string; lastReadingAt?: string | null },
  now: number = Date.now(),
): number | null {
  if (r.status !== 'OPEN') return null;
  const last = lastActivity(r);
  if (last <= 0) return null;
  const quiet = now - last;
  return quiet > SHIFT_MS ? quiet : null;
}
